import { useTranslation } from '../presentation/common/useTranslation.js';
import { PresentationModeSwitch } from '../presentation/PresentationProvider';
import GoodvaleWordmark from './GoodvaleWordmark';
import { useState } from 'react';
import { Outlet, Link, NavLink } from 'react-router-dom';

import { Shield, Menu, X } from 'lucide-react';
import GoodvaleLogo from './GoodvaleLogo';
import LanguageSwitcher from './LanguageSwitcher';
import ThemeSwitcher from './ThemeSwitcher';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

import { openCookieBanner } from './CookieBanner';

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export default function PublicLayout() {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { name: t('nav.mechanics', 'Spielmechanik'), href: '/mechanics' },
    { name: t('nav.classes', 'Klassen'), href: '/classes' },
    { name: t('nav.ranks', 'Ränge'), href: '/ranks' },
    { name: t('nav.categories', 'Kategorien'), href: '/categories' },
    { name: t('nav.good_deeds', 'Gute Taten'), href: '/good-deeds' },
    { name: t('partnersPage.nav'), href: '/partners' },
    { name: t('nav.faq', 'FAQ'), href: '/faq' },
  ];

  return (
    <div className="public-shell min-h-screen bg-[#061018] text-[#f0ede6] flex flex-col selection:bg-[#7c3aed]/40 selection:text-white">
      {/* Public Topbar Navigation */}
      <header className="public-header border-b border-[#bda16b]/20 bg-[#061018]/90 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <GoodvaleLogo className="w-8 h-8 rounded-md group-hover:scale-105 transition-transform" />
            <GoodvaleWordmark />
          </Link>

          {/* Desktop navigation */}
          <div className="hidden xl:flex items-center gap-5">
            <nav className="flex items-center gap-6">
              {navLinks.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  className={({ isActive }) =>
                    cn(
                      'public-nav-link text-sm transition-colors',
                      isActive ? 'text-[#eed8ab] font-medium active' : 'text-[#b5b1cb] hover:text-[#f0ede6]'
                    )
                  }
                >
                  {item.name}
                </NavLink>
              ))}
            </nav>

            <div className="h-4 w-px bg-[#344857]/70" />

            {/* Language & Theme Switchers */}
            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <ThemeSwitcher variant="toggle" />
              <PresentationModeSwitch />
            </div>

            {/* Auth CTA Buttons */}
            <div className="flex items-center gap-3">
              <Link 
                to="/login" 
                className="public-login-btn px-4 py-1.5 text-xs font-serif text-[#e9d5ab] hover:text-[#f5e6c8] bg-[#0b1923] border border-[#9d8253] rounded-full transition-colors"
              >
                {t('common.login')}
              </Link>
              <Link 
                to="/register" 
                className="public-register-btn px-4 py-1.5 text-xs font-serif font-semibold bg-gradient-to-r from-[#e0bd78] to-[#b48a46] text-[#08131d] hover:brightness-110 border border-[#edcd94] rounded-full shadow-[0_0_12px_rgba(217,181,109,0.2)] transition-all"
              >
                {t('common.register')}
              </Link>
            </div>
          </div>

          {/* Mobile menu trigger */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="xl:hidden p-2 text-zinc-400 hover:text-zinc-200 focus:outline-none transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu drawer */}
        {mobileMenuOpen && (
          <div className="public-mobile-drawer xl:hidden border-t border-zinc-800 bg-zinc-950/95 backdrop-blur px-6 py-5 space-y-4">
            <nav className="flex flex-col gap-3">
              {navLinks.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'public-nav-link text-sm font-medium transition-colors py-1.5',
                      isActive ? 'text-zinc-100 font-semibold active' : 'text-zinc-400 hover:text-zinc-200'
                    )
                  }
                >
                  {item.name}
                </NavLink>
              ))}
            </nav>
            
            <div className="h-px bg-zinc-800/80 w-full" />
            
            <div className="flex items-center justify-between py-1">
              <span className="text-xs text-zinc-500 font-mono">Sprache / Language</span>
              <LanguageSwitcher />
            </div>

            <div className="flex items-center justify-between py-1">
              <span className="text-xs text-zinc-500 font-mono">Modus / Theme</span>
              <div className="flex items-center gap-2">
                <ThemeSwitcher variant="toggle" />
                <PresentationModeSwitch />
              </div>
            </div>


            <div className="h-px bg-zinc-800/80 w-full" />

            <div className="flex flex-col gap-2 pt-1">
              <Link 
                to="/login" 
                onClick={() => setMobileMenuOpen(false)}
                className="public-login-btn w-full py-2.5 text-center text-xs font-serif text-[#e9d5ab] hover:text-[#f5e6c8] bg-[#0b1923] border border-[#9d8253] rounded-full transition-colors"
              >
                {t('common.login')}
              </Link>
              <Link 
                to="/register" 
                onClick={() => setMobileMenuOpen(false)}
                className="public-register-btn w-full py-2.5 text-center text-xs font-serif font-semibold bg-gradient-to-r from-[#e0bd78] to-[#b48a46] text-[#08131d] hover:brightness-110 border border-[#edcd94] rounded-full shadow-[0_0_12px_rgba(217,181,109,0.2)] transition-all"
              >
                {t('common.register')}
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Main Public Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Public Footer */}
      <footer className="public-footer border-t border-[#bda16b]/20 bg-[#061018] py-10 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#b5b1cb]">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#d9b56d]" />
            <span>© {new Date().getFullYear()} Goodvale. All rights reserved.</span>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            <Link to="/mechanics" className="hover:text-[#eed8ab] transition-colors">
              {t('nav.mechanics', 'Spielmechanik')}
            </Link>
            <Link to="/classes" className="hover:text-[#eed8ab] transition-colors">
              {t('nav.classes', 'Klassen')}
            </Link>
            <Link to="/ranks" className="hover:text-[#eed8ab] transition-colors">{t('nav.ranks', 'Ränge')}</Link>
            <Link to="/categories" className="hover:text-[#eed8ab] transition-colors">{t('nav.categories', 'Kategorien')}</Link>
            <Link to="/good-deeds" className="hover:text-[#eed8ab] transition-colors">{t('nav.good_deeds', 'Gute Taten')}</Link>
            <Link to="/faq" className="hover:text-[#eed8ab] transition-colors">
              {t('nav.faq', 'FAQ')}
            </Link>
            <Link to="/legal" className="hover:text-[#eed8ab] transition-colors">
              {t('nav.legal', 'Rechtliches')}
            </Link>
            <button
              onClick={() => openCookieBanner()}
              className="hover:text-[#eed8ab] transition-colors cursor-pointer"
            >
              {t('cookie.settings', 'Cookie-Einstellungen')}
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}


